import type { DataConnection, Peer, PeerError } from 'peerjs';
import {
  ConnectionHandlers,
  NetError,
  type Connection,
  type HostEndpoint,
  type NetworkProvider,
} from './transport';

/** Namespaces our peer ids on the shared public PeerJS signalling server. */
const PEER_PREFIX = 'petal-plains-v1-';
const CONNECT_TIMEOUT_MS = 20_000;

type PeerConstructor = typeof Peer;

let peerModule: Promise<PeerConstructor> | null = null;

/** PeerJS is loaded lazily so solo players never download it. */
function loadPeer(): Promise<PeerConstructor> {
  peerModule ??= import('peerjs').then((m) => m.Peer);
  return peerModule;
}

function toNetError(error: PeerError<string> | Error): NetError {
  const type = (error as { type?: string }).type;
  switch (type) {
    case 'unavailable-id':
      return new NetError('code-taken', 'That farm code is already in use.');
    case 'peer-unavailable':
      return new NetError(
        'not-found',
        "Couldn't find a farm with that code. Check it and try again!",
      );
    case 'browser-incompatible':
      return new NetError('unsupported', "Your browser doesn't support peer-to-peer connections.");
    default:
      return new NetError(
        'unreachable',
        "Couldn't reach the multiplayer server. Check your connection and try again.",
      );
  }
}

class PeerConnection implements Connection {
  private readonly handlers = new ConnectionHandlers();

  constructor(
    private readonly connection: DataConnection,
    private readonly onClosed: () => void = () => undefined,
  ) {
    connection.on('data', (data) => this.handlers.message(data));
    connection.on('close', () => this.finish());
    connection.on('error', () => this.finish());
  }

  get id(): string {
    return this.connection.peer;
  }

  send(message: unknown): void {
    if (!this.connection.open) return;
    try {
      void this.connection.send(message);
    } catch (error) {
      console.warn('Petal Plains: failed to send', error);
    }
  }

  onMessage(handler: (data: unknown) => void): void {
    this.handlers.onMessage(handler);
  }

  onClose(handler: () => void): void {
    this.handlers.onClose(handler);
  }

  close(): void {
    this.connection.close();
    this.finish();
  }

  private finish(): void {
    if (this.handlers.isClosed) return;
    this.handlers.close();
    this.onClosed();
  }
}

class PeerHost implements HostEndpoint {
  private readonly handlers: Array<(connection: Connection) => void> = [];
  private readonly lostHandlers: Array<(reason: string) => void> = [];
  private closing = false;

  constructor(
    private readonly peer: Peer,
    readonly code: string,
  ) {
    peer.on('connection', (dataConnection) => {
      dataConnection.on('open', () => {
        const connection = new PeerConnection(dataConnection);
        for (const handler of this.handlers) handler(connection);
      });
    });
    // Once open, errors are per-visitor ('webrtc', 'peer-unavailable') or about the
    // signalling socket ('network', 'server-error'…). None of them should take the
    // farm down: existing visitors keep their direct connections.
    peer.on('error', (error) => console.warn('Petal Plains: host peer error', error.type));
    // Losing the signalling socket doesn't drop existing peers; try to get it back
    // so new friends can still join.
    peer.on('disconnected', () => {
      if (!peer.destroyed) setTimeout(() => !peer.destroyed && peer.reconnect(), 2000);
    });
    peer.on('close', () => {
      if (this.closing) return;
      for (const handler of this.lostHandlers)
        handler('Your farm lost its connection. Open it again to invite friends.');
    });
  }

  onConnection(handler: (connection: Connection) => void): void {
    this.handlers.push(handler);
  }

  onLost(handler: (reason: string) => void): void {
    this.lostHandlers.push(handler);
  }

  close(): void {
    this.closing = true;
    this.peer.destroy();
  }
}

/** WebRTC data channels brokered by the free public PeerJS server. */
export class PeerNetwork implements NetworkProvider {
  async host(code: string): Promise<HostEndpoint> {
    const PeerCtor = await loadPeer();
    return new Promise((resolve, reject) => {
      const peer = new PeerCtor(PEER_PREFIX + code, { debug: 0 });
      const timer = setTimeout(() => {
        peer.destroy();
        reject(new NetError('timeout', 'The multiplayer server took too long to answer.'));
      }, CONNECT_TIMEOUT_MS);
      // Errors before 'open' (e.g. the code is taken) are fatal; after that the
      // long-lived handler in PeerHost takes over, so detach this one.
      const onStartupError = (error: PeerError<string>) => {
        clearTimeout(timer);
        peer.destroy();
        reject(toNetError(error));
      };
      peer.on('error', onStartupError);
      peer.once('open', () => {
        clearTimeout(timer);
        peer.off('error', onStartupError);
        resolve(new PeerHost(peer, code));
      });
    });
  }

  async join(code: string): Promise<Connection> {
    const PeerCtor = await loadPeer();
    return new Promise((resolve, reject) => {
      const peer = new PeerCtor({ debug: 0 });
      let settled = false;
      const fail = (error: NetError) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        peer.destroy();
        reject(error);
      };
      const timer = setTimeout(
        () =>
          fail(
            new NetError(
              'timeout',
              "Couldn't connect to that farm. Your network may be blocking peer-to-peer connections.",
            ),
          ),
        CONNECT_TIMEOUT_MS,
      );
      peer.on('error', (error) => fail(toNetError(error)));
      peer.once('open', () => {
        const dataConnection = peer.connect(PEER_PREFIX + code, {
          reliable: true,
          serialization: 'json',
        });
        dataConnection.once('open', () => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(new PeerConnection(dataConnection, () => peer.destroy()));
        });
      });
    });
  }
}
